/**
 * API Configuration
 *
 * Manages environment-based API URLs and configuration
 */

/**
 * Environment type
 */
export type Environment = 'development' | 'staging' | 'production';

/**
 * Get current environment from environment variables
 */
export function getEnvironment(): Environment {
  const env = import.meta.env.VITE_ENVIRONMENT as string | undefined;

  if (env === 'staging' || env === 'production') {
    return env;
  }

  return 'development';
}

/**
 * Get the base API URL from environment variables
 * Supports environment-specific URLs and fallback logic
 */
export function getApiBaseUrl(): string {
  // Vite exposes env variables as import.meta.env
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;

  if (!baseUrl) {
    const env = getEnvironment();

    // Environment-specific defaults
    const defaults: Record<Environment, string> = {
      development: 'http://localhost:5000',
      staging: 'https://api-staging.mentionedme.com',
      production: 'https://api.mentionedme.com'
    };

    const defaultUrl = defaults[env];
    console.warn(`VITE_API_BASE_URL not set, using default for ${env}: ${defaultUrl}`);
    return defaultUrl;
  }

  return baseUrl;
}

/**
 * Get API timeout from environment variables
 */
export function getApiTimeout(): number {
  const timeout = import.meta.env.VITE_API_TIMEOUT as string | undefined;

  if (timeout) {
    const parsed = parseInt(timeout, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }

  // Default timeout: 15 minutes (for long-running operations like OSM imports)
  // OSM imports for large areas can take 10+ minutes
  return 900000;
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * API endpoint paths
 */
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    CHECK_EMAIL: '/api/auth/check-email', // Check email availability for registration
    REFRESH_TOKEN: '/api/auth/refresh', // Backend uses /refresh not /refresh-token
    LOGOUT: '/api/auth/logout',
    VERIFY: '/api/auth/verify', // Verify authentication status
    REVOKE: '/api/auth/revoke', // Revoke current token
    HEALTH: '/api/auth/health', // Health check
    FORGOT_PASSWORD: '/api/auth/forgot-password', // Request password reset
    RESET_PASSWORD: '/api/auth/reset-password', // Reset password with token
    RESEND_EMAIL_CONFIRMATION: '/api/auth/resend-email-confirmation' // Resend confirmation email
  },

  // Reddit Mentions
  REDDIT_MENTIONS: {
    BASE: '/api/reddit-mentions',
    BY_ID: (id: string): string => `/api/reddit-mentions/${id}`,
    BY_BUSINESS: (businessId: string): string => `/api/reddit-mentions/business/${businessId}`,
    VERIFY: (id: string): string => `/api/reddit-mentions/${id}/verify`, // Backend uses single verify endpoint
    CONFIRM: (id: string): string => `/api/reddit-mentions/${id}/confirm`, // Session 003: Confirm mention and link to business
    DISMISS: (id: string): string => `/api/reddit-mentions/${id}/dismiss`, // Session 003: Dismiss mention as false positive
    BULK_IMPORT: '/api/reddit-mentions/bulk-import',
    PROCESS_UNMATCHED: '/api/reddit-mentions/process-unmatched',
    MATCH_CONFIDENCE: (id: string): string => `/api/reddit-mentions/${id}/match-confidence`,
    RECALCULATE_CONFIDENCE: (id: string): string => `/api/reddit-mentions/${id}/recalculate-match-confidence`
  },

  // Businesses
  BUSINESSES: {
    BASE: '/api/businesses',
    BY_ID: (id: string): string => `/api/businesses/${id}`,
    REACTIVATE: (id: string): string => `/api/businesses/${id}/reactivate`,
    ADMIN_ADDRESS: (id: string): string => `/api/businesses/${id}/admin-address`,
    SEARCH: '/api/businesses/search', // Session 004: Business autocomplete search
    SUMMARY_BY_ID: (id: string): string => `/api/businesses/${id}/summary`, // Session 276: Public endpoint for registration pre-population
    BY_COMPANY_ID: (companyId: string): string => `/api/businesses/by-company/${companyId}` // Session 276: Fast company lookup for registration
  },

  // Regions
  REGIONS: {
    BASE: '/api/regions',
    BY_ID: (id: string): string => `/api/regions/${id}`,
    DROPDOWN: '/api/regions/dropdown'
  },

  // Users (Admin only)
  USERS: {
    BASE: '/api/users',
    BY_ID: (id: string): string => `/api/users/${id}`,
    ASSIGN_BUSINESS: (id: string): string => `/api/users/${id}/assign-business`,
    REMOVE_BUSINESS: (id: string): string => `/api/users/${id}/business`
  },

  // Subscriptions
  SUBSCRIPTIONS: {
    CREATE: '/api/subscriptions',
    CANCEL: (id: string): string => `/api/subscriptions/${id}`,
    GET_MY: '/api/subscriptions/me',
    GET_ACCESS: '/api/subscriptions/access'
  },

  // Payments (SESSION_192: Payment method validation)
  PAYMENTS: {
    CREATE_SETUP_INTENT: '/api/payments/setup-intent',
    GET_PAYMENT_METHOD: (id: string): string => `/api/payments/payment-method/${id}`
  },

  // Customer Mentions (Sprint 037 Session 9: Self-confirmation UI)
  CUSTOMER_MENTIONS: {
    PENDING: (businessId: string): string => `/api/businesses/${businessId}/mentions/pending`,
    CONFIRM: (businessId: string, mentionId: string): string =>
      `/api/businesses/${businessId}/mentions/${mentionId}/customer-confirm`,
    REJECT: (businessId: string, mentionId: string): string =>
      `/api/businesses/${businessId}/mentions/${mentionId}/customer-reject`
  }
} as const;

/**
 * API configuration constants
 */
export const API_CONFIG = {
  get TIMEOUT(): number {
    return getApiTimeout();
  },
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000, // 1 second
  RETRY_STATUS_CODES: [408, 429, 500, 502, 503, 504] // Retry on these status codes
} as const;
